import { Component, Inject, ViewEncapsulation } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { fuseAnimations } from "@fuse/animations";
import { ProfissaoService } from "app/auxiliares/services/profissao.service";
import { UsuarioService } from "app/base-module/base-service/usuario.service";
import { AbordagemService } from "app/gestao-patrimonial/services/abordagem.service";
import { Usuario } from "app/usuarios/models/user.model";
import { BaseComponent } from "../../base-module/base-component/base.component";
import { ClienteService } from "../services/cliente.service";
import { EstadoCivilService } from "../services/estado-civil.service";

export interface DialogData {
    abordagem: boolean;
}

@Component({
    selector: "app-cadastro-cliente-simples",
    templateUrl: "./cadastro-cliente-simples.component.html",
    styleUrls: ["./cadastro-cliente-simples.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    providers: [AbordagemService],
})
export class CadastroClienteSimplesComponent extends BaseComponent {
    nome = new FormControl("", [Validators.required]);
    data_nascimento = new FormControl();
    sexo = new FormControl("", [Validators.required]);
    estado_civil_id = new FormControl("");
    profissao_id = new FormControl("");
    assessor_id = new FormControl("", [Validators.required]);
    prospect = new FormControl("", [Validators.required]);
    telefone = new FormControl();

    model: any = {
        nome: "",
    };

    listAssessor: Usuario[] = [];
    listProfissao: any[] = [];
    listEstadoCivil: any[] = [];
    loadingSpinnerAssessor: boolean = false;
    loadingSpinnerProfissao: boolean = false;
    loadingSpinnerEstadoCivil: boolean = false;

    constructor(
        public service: ClienteService,
        private _abordagemService: AbordagemService,
        public dialog: MatDialog,
        override snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<CadastroClienteSimplesComponent>,
        private usuarioService: UsuarioService,
        private _profissaoService: ProfissaoService,
        private _estadoCivilService: EstadoCivilService,
        @Inject(MAT_DIALOG_DATA) public data: DialogData
    ) {
        super(dialog, snackBar);

        this.getAssessores();
        this.getProfissao();
        this.getEstadoCivil();
    }

    /**
     * Fecha o diálogo cancelando a operação
     */
    onNoClick(): void {
        this.dialogRef.close("cancel");
    }

    /**
     * Verifica se o formulário está inválido
     * @returns true se algum campo obrigatório estiver inválido, false caso contrário
     */
    isInvalidForm(): boolean {
        if (!this.nome.valid) {
            return true;
        }

        if (!this.sexo.valid) {
            return true;
        }

        if (!this.assessor_id.valid) {
            return true;
        }

        if (!this.telefone.valid) {
            return true;
        }

        return false;
    }

    /**
     * Salva o cliente e, se necessário, gera uma abordagem
     */
    salvar(): void {
        this.openLoading();
        this.model.nome = this.nome.value;
        this.model.estado_civil_id = this.estado_civil_id.value;
        if (this.profissao_id.value !== "") {
            this.model.profissao_id = this.profissao_id.value;
        }
        this.model.data_nascimento = this.data_nascimento.value;
        this.model.sexo = this.sexo.value;
        this.model.assessor_id = this.assessor_id.value;
        this.model.prospect = this.prospect.value;
        this.model.telefone = this.telefone.value;

        this.service.create(this.model).subscribe({
            next: (data) => {
                if (data && this.data.abordagem) {
                    this.gerarAbordagem(data.data.id);
                } else {
                    this.closeLoading();
                    this.dialogRef.close(data);
                }
            },
            error: (error) => {
                this.closeLoading();
                this.showError(`Erro ao cadastrar cliente: ${error.error?.message || error.message}`);
            },
            complete: () => {
                // Loading será fechado em next ou error
            },
        });
    }

    /**
     * Gera uma abordagem para o cliente recém-cadastrado
     * @param cliente_id - Identificador único do cliente
     */
    gerarAbordagem(cliente_id: number): void {
        const abordagem = {
            cliente_id: cliente_id,
            data_abordagem: new Date(),
            descricao: "Planejamento Financeiro",
        };

        this._abordagemService.create(abordagem).subscribe({
            next: (data) => {
                this.closeLoading();
                this.dialogRef.close(data);
            },
            error: (error) => {
                this.closeLoading();
                this.showError(`Erro ao gerar abordagem: ${error.error?.message || error.message}`);
            },
        });
    }

    /**
     * Retorna a mensagem de erro do campo nome
     * @returns Mensagem de erro ou string vazia
     */
    getErrorNameMessage(): string {
        return this.nome.hasError("required") ? "Nome é um campo obrigatório" : "";
    }

    /**
     * Retorna a mensagem de erro do campo assessor
     * @returns Mensagem de erro ou string vazia
     */
    getErrorAssessorMessage(): string {
        return this.assessor_id.hasError("required") ? "Assessor é um campo obrigatório" : "";
    }

    /**
     * Busca a lista de assessores disponíveis
     */
    getAssessores(): void {
        this.loadingSpinnerAssessor = true;
        this.usuarioService.getLista({ tipo_usuario_id: "todos" }).subscribe({
            next: (request) => {
                this.listAssessor = request.data ?? [];
            },
            error: (error) => {
                this.loadingSpinnerAssessor = false;
                this.showError(`Erro ao buscar assessores: ${error.error?.message || error.message}`);
            },
            complete: () => {
                this.loadingSpinnerAssessor = false;
            },
        });
    }

    /**
     * Busca a lista de profissões disponíveis
     */
    getProfissao(): void {
        this.loadingSpinnerProfissao = true;
        this._profissaoService.getLista().subscribe({
            next: (request) => {
                this.listProfissao = request.data;
            },
            error: (error) => {
                this.loadingSpinnerProfissao = false;
                this.showError(`Erro ao buscar profissões: ${error.error?.message || error.message}`);
            },
            complete: () => {
                this.loadingSpinnerProfissao = false;
            },
        });
    }

    /**
     * Busca a lista de estados civis disponíveis
     */
    getEstadoCivil(): void {
        this.loadingSpinnerEstadoCivil = true;
        this._estadoCivilService.getLista().subscribe({
            next: (request) => {
                this.listEstadoCivil = request.data;
            },
            error: (error) => {
                this.loadingSpinnerEstadoCivil = false;
                this.showError(`Erro ao buscar estados civis: ${error.error?.message || error.message}`);
            },
            complete: () => {
                this.loadingSpinnerEstadoCivil = false;
            },
        });
    }
}
